import type { CSSProperties, ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import type { DashboardZoneId } from '@/products/helpdesk/features/dashboard/dashboard-layout';
import { cn } from '@/lib/utils';

export function DashboardZoneShell({
  zoneId,
  title,
  description,
  headingId,
  beforeHeader,
  headerAction,
  dragHandle,
  moveControls,
  children,
  className,
  editMode = false,
  setNodeRef,
  style,
  isDragging = false,
}: {
  zoneId: DashboardZoneId;
  title: string;
  description?: string;
  headingId: string;
  /** e.g. Major Incidents banner — rendered above the zone title */
  beforeHeader?: ReactNode;
  headerAction?: ReactNode;
  dragHandle?: ReactNode;
  moveControls?: ReactNode;
  children: ReactNode;
  className?: string;
  editMode?: boolean;
  setNodeRef?: (node: HTMLElement | null) => void;
  style?: CSSProperties;
  isDragging?: boolean;
}) {
  return (
    <section
      ref={setNodeRef}
      style={style}
      data-dashboard-zone={zoneId}
      aria-labelledby={headingId}
      className={cn(
        'min-w-0',
        beforeHeader ? 'space-y-8' : 'space-y-3',
        editMode && 'rounded-xl ring-1 ring-border/80 ring-offset-2 ring-offset-background',
        isDragging && 'opacity-60',
        className,
      )}
    >
      {beforeHeader}
      <div className="min-w-0 space-y-3">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-2">
            {dragHandle}
            <div className="min-w-0">
              <h2 id={headingId} className="text-lg font-semibold tracking-tight">
                {title}
              </h2>
              {description ? (
                <p className="text-sm break-words text-muted-foreground">{description}</p>
              ) : null}
            </div>
          </div>
          {moveControls || headerAction ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {moveControls}
              {headerAction}
            </div>
          ) : null}
        </div>
        {children}
      </div>
    </section>
  );
}

export function DashboardEditHideButton({
  label,
  onClick,
}: {
  /** Accessible name; short visible text stays „Ausblenden“ when label is long */
  label: string;
  onClick: () => void;
}) {
  const visible = label.endsWith(' ausblenden') ? 'Ausblenden' : label;
  return (
    <Button type="button" variant="outline" size="sm" aria-label={label} onClick={onClick}>
      {visible}
    </Button>
  );
}

export function DashboardWidgetEditFrame({
  editMode,
  onHide,
  hideLabel,
  dragHandle,
  moveControls,
  children,
}: {
  editMode: boolean;
  onHide?: () => void;
  hideLabel: string;
  dragHandle?: ReactNode;
  moveControls?: ReactNode;
  children: ReactNode;
}) {
  if (!editMode) {
    return children;
  }

  return (
    <div className="relative rounded-xl ring-1 ring-border/70">
      <div className="absolute top-2 left-2 z-10 flex items-center gap-1">
        {dragHandle}
        {moveControls}
      </div>
      {onHide ? (
        <div className="absolute top-2 right-2 z-10">
          <DashboardEditHideButton label={hideLabel} onClick={onHide} />
        </div>
      ) : null}
      <div
        className={cn(
          onHide || dragHandle ? 'pt-12 sm:pt-10' : undefined,
          dragHandle ? 'sm:pl-20' : undefined,
          onHide ? 'sm:pr-28' : undefined,
        )}
      >
        {children}
      </div>
    </div>
  );
}
